<template>
  <div class="sub-family-option-card">
    <pdl-card :qaid="`sub-family-option-card-${idFromFamilyName}`">
      <template #bodyContent>
        <input
          :id="`option-${idFromFamilyName}`"
          v-model="localValue"
          :value="subfamily"
          :checked="isSelected"
          type="radio"
          :name="`sub-family-option-${groupName}`"
          class="absolute block top-0 left-0 right-0 bottom-0 w-full h-full pointer-events-auto cursor-pointer z-10 rounded-lg"
        />
        <div class="relative z-20">
          <pdl-label
            v-if="isOnSale && assetId"
            class="absolute top-2 right-2"
            kind="critical"
            qaid="frame-type-sale-label"
          >
            {{ $t('product.sale.flag') }}
          </pdl-label>
          <image-component
            v-if="assetId"
            :asset-id="assetId"
            :alt-text="familyName"
            loading="lazy"
            aspect-ratio="8x3"
            transformation="fill"
            :qaid="`sub-family-image-${idFromFamilyName}`"
          />
          <div class="sub-family-details transition-colors" :qaid="`sub-family-detail-${idFromFamilyName}`">
            <div class="flex justify-between items-center">
              <label
                :for="`option-${idFromFamilyName}`"
                class="text-sm font-bold"
                :qaid="`sub-family-name-${idFromFamilyName}`"
              >
                {{ familyName }}
              </label>
              <div
                v-if="startingAtPrice"
                class="text-sm"
                :qaid="`sub-family-price-${idFromFamilyName}`"
                v-html="$t('projectOne.card.startingAt', [startingAtPrice])"
              />
            </div>
          </div>
        </div>
      </template>
    </pdl-card>
  </div>
</template>

<script>
import {PdlCard} from '@pedal/pdl-card';
import {PdlLabel} from '@pedal/pdl-label';
import {mapGetters} from 'vuex';
import {toKebabCase} from '@/utils/util';
import ImageComponent from '@/components/ImageComponent.vue';

export default {
  components: {
    PdlCard,
    PdlLabel,
    ImageComponent,
  },
  props: {
    subfamily: {
      type: Object,
      required: true,
    },
    modelValue: {
      type: Object,
      required: true,
    },
    groupName: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapGetters('familyDetails', ['isSubFamilyOnSale']),

    selectedClass() {
      return {
        'shadow-none': !this.isSelected,
        selected: this.isSelected,
      };
    },

    isOnSale() {
      if (!this.subfamily?.models) return false;
      return this.isSubFamilyOnSale(this.subfamily.models);
    },

    startingAtPrice() {
      if (!this.subfamily?.startingAtPrice?.formatted) return null;
      return this.isOnSale
        ? `<span class="text-red-100">${this.subfamily?.startingAtPrice?.formatted}</span>`
        : this.subfamily?.startingAtPrice?.formatted;
    },

    isSelected() {
      return this.subfamily?.familyCode === this.modelValue?.familyCode;
    },

    familyName() {
      return this.subfamily?.familyName;
    },
    idFromFamilyName() {
      return toKebabCase(this.subfamily?.familyName);
    },
    familyCode() {
      return this.subfamily?.familyCode;
    },

    assetId() {
      return this.subfamily?.assetId;
    },
    localValue: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.sub-family-option-card {
  scale: 1;

  &:has(input[type='radio']:active) {
    scale: 0.99;
  }
}

.sub-family-details {
  padding: 0.625rem;
}

:deep(.pdl-card__body) {
  padding: 0;
  pointer-events: none;
  background-color: var(--white);
}

input[type='radio'] {
  appearance: none;
  color: var(--gray-20);
  line-height: 0;
  transition: box-shadow 0.2s, background-color 0.2s, color 0.2s, transform 0.1s;
  border: 1px solid currentColor;
  background-color: var(--white);
  box-shadow: inset 0 0 0 #007ab800;

  &:checked {
    color: var(--blue-100, #007ab8);
    background-color: var(--blue-10, #e6f2f8);
  }

  @media (hover: hover) {
    &:hover {
      color: var(--blue-100, #007ab8);
      box-shadow: inset 0 0 2px 1px var(--blue-100, #007ab8);
      background-color: var(--blue-10, #e6f2f8);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--blue-100);
    background-color: var(--blue-10, #e6f2f8);
    outline-offset: 0.125rem;
  }
}
</style>
